import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowLeft, Calendar, Smile, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { CALENDLY_URL } from '@/lib/site';

const benefits = [
  'Faster load times',
  'Better conversions',
  'Better performing ads',
  'More secure',
  'Better ranking on Google',
  'Boosts your Google Business Profile in the Maps Listings',
  'Better Load times leads to higher conversions',
];

const WebDesign = () => (
  <div className="min-h-screen pt-16 bg-gradient-to-b from-primary/10 to-background dark:from-primary/5">
    <Helmet>
      <title>Website Design in the USA | Omnicraft Services</title>
      <meta name="description" content="Discover why Omnicraft Services hand-codes every website for speed, security, and SEO. No page builders or WordPress. Learn more about our custom web design philosophy." />
    </Helmet>
    {/* Hero Section */}
    <section className="py-12 md:py-20">
      <div className="container-custom max-w-4xl mx-auto text-center">
        <Link to="/services/web-software-development" className="inline-flex items-center text-primary mb-6 hover:underline text-sm md:text-base">
          <ArrowLeft size={16} className="mr-2" /> Back to Web Design & Software Development
        </Link>
        <div className="flex flex-col items-center justify-center mb-4">
          <Smile size={48} className="text-primary mb-2" />
          <h1 className="text-4xl md:text-5xl font-bold mb-2">Friendly, Custom Website Design</h1>
        </div>
        <p className="text-lg md:text-xl text-slate-700 dark:text-slate-300 mb-6">
          Welcome! At <span className="font-semibold text-primary">Omnicraft Services</span>, we believe your website should be as unique and friendly as your business. We hand-code every site—no page builders, no WordPress bloat—so your online presence is fast, secure, and truly yours.
        </p>
      </div>
    </section>

    {/* Why It Matters */}
    <section className="py-8 md:py-12 bg-slate-50 dark:bg-slate-900/50 border-y border-primary/10">
      <div className="container-custom max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-primary flex items-center justify-center gap-2">
          <Star className="text-yellow-400" size={28} /> Why It Matters
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {benefits.map((benefit, idx) => (
            <Card key={idx} className="flex items-center p-4 bg-white/80 dark:bg-slate-800/80 border border-primary/20 shadow-sm">
              <CheckCircle className="text-primary mr-3 flex-shrink-0" size={28} />
              <span className="text-base md:text-lg">{benefit}</span>
            </Card>
          ))}
        </div>
      </div>
    </section>

    {/* Details Section */}
    <section className="py-12 md:py-16">
      <div className="container-custom max-w-3xl mx-auto">
        <Card className="bg-primary/5 border-primary/20 shadow-md">
          <CardContent className="p-6 md:p-8">
            <h3 className="text-xl font-semibold mb-4">Why Custom Code?</h3>
            <p className="mb-4">It's tough out there! Your website needs every edge to stand out. When most sites use page builders and load slowly, you can shine with a custom-coded site that loads instantly. If your site takes more than 3 seconds to load, you could lose up to 50% of your visitors. Our sites are so fast, your leads won't slip away.</p>
            <h4 className="text-lg font-semibold mb-2 mt-6">Better Ads Performance</h4>
            <p className="mb-4">Faster sites mean better conversions and lower ad costs. Google rewards speed with lower cost-per-click, saving you money every month.</p>
            <h4 className="text-lg font-semibold mb-2 mt-6">Custom Coded Websites Are More Secure</h4>
            <p className="mb-4">No plugins, no databases, no worries! Our static sites are nearly impossible to hack and require almost no maintenance. You can relax knowing your site is safe and sound.</p>
            <h4 className="text-lg font-semibold mb-2 mt-6">Improve Your Search Ranking</h4>
            <p className="mb-4">Google loves fast sites. Better load times mean better rankings, more visitors, and more business for you.</p>
            <h4 className="text-lg font-semibold mb-2 mt-6">Boost Your Google Business Profile</h4>
            <p className="mb-4">A speedy site helps your Google Business Profile shine in Maps searches. Pair that with great reviews and a complete profile, and you'll see more leads than ever.</p>
            <h4 className="text-lg font-semibold mb-2 mt-6">Small Business SEO Specialists</h4>
            <p className="mb-4">We don't just build sites—we help you grow. Our in-house SEO expert can help you rank, blog, and build backlinks. Want to know more? Let's chat!</p>
          </CardContent>
        </Card>
      </div>
    </section>

    {/* Pricing Section */}
    <section className="py-12 md:py-20">
      <div className="container-custom max-w-6xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 text-primary">Pricing</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Lump Sum */}
          <Card className="bg-black/90 border border-primary/20 shadow-lg flex flex-col justify-between">
            <CardContent className="p-8 flex flex-col h-full">
              <h3 className="text-2xl font-bold mb-4 text-white">LUMP SUM</h3>
              <ul className="mb-8 space-y-2 text-white/90">
                <li>Design And Development <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>$17.50/mo Hosting <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>$70 fee Per Page after 5 <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>+$35/mo Unlimited Edits Add-on <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>+$175 To Add A Blog <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li className="text-slate-400 flex items-center">24/7 Support <span className="ml-2">&#10060;</span></li>
                <li className="text-slate-400 flex items-center">Lifetime Updates <span className="ml-2">&#10060;</span></li>
              </ul>
              <div className="mt-auto">
                <div className="text-4xl font-bold text-white mb-2">$2660</div>
                <div className="text-white/70 mb-4">+ $17.50/mo Hosting</div>
                <Button asChild size="lg" className="w-full bg-gradient-to-r from-primary to-secondary text-white font-bold">
                  <Link to="/contact">GET STARTED</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
          {/* Monthly */}
          <Card className="bg-black/90 border border-primary/20 shadow-lg flex flex-col justify-between ring-2 ring-primary/60">
            <CardContent className="p-8 flex flex-col h-full">
              <h3 className="text-2xl font-bold mb-4 text-white">MONTHLY</h3>
              <ul className="mb-8 space-y-2 text-white/90">
                <li>Design And Development <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>Includes Hosting <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>$70 fee Per Page After 5 <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>+$175 To Add A Blog <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>Unlimited Edits <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>24/7 Support <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>Lifetime Updates <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
              </ul>
              <div className="mt-auto">
                <div className="text-4xl font-bold text-white mb-2">$122.50</div>
                <div className="text-white/70 mb-4">Per Month</div>
                <Button asChild size="lg" className="w-full bg-gradient-to-r from-primary to-secondary text-white font-bold">
                  <Link to="/contact">GET STARTED</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
          {/* Ecommerce */}
          <Card className="bg-black/90 border border-primary/20 shadow-lg flex flex-col justify-between">
            <CardContent className="p-8 flex flex-col h-full">
              <h3 className="text-2xl font-bold mb-4 text-white">ECOMMERCE</h3>
              <ul className="mb-8 space-y-2 text-white/90">
                <li>Custom Shopify Store <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>Configure Any And All Apps <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>Integrated Shipping <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>Shopify Tutorial Walkthrough <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>Fully Editable In Shopify CMS <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li className="text-slate-400 flex items-center">+$35/mo Unlimited Edits <span className="ml-2">&#10060;</span></li>
                <li className="text-slate-400 flex items-center">24/7 Support <span className="ml-2">&#10060;</span></li>
              </ul>
              <div className="mt-auto">
                <div className="text-4xl font-bold text-white mb-2">$5600</div>
                <div className="text-white/70 mb-4">Starting</div>
                <Button asChild size="lg" className="w-full bg-gradient-to-r from-primary to-secondary text-white font-bold">
                  <Link to="/contact">GET STARTED</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>

    {/* Testimonial Section */}
    <section className="py-10 md:py-16 bg-slate-100 dark:bg-slate-800 border-t border-primary/10">
      <div className="container-custom max-w-2xl mx-auto text-center">
        <div className="flex flex-col items-center mb-4">
          <Star className="text-yellow-400 mb-2" size={36} />
          <p className="italic text-lg md:text-xl text-slate-700 dark:text-slate-200 mb-2">“Our new website from Omnicraft Services is so fast and easy to use. We've seen more leads and happier customers since launch!”</p>
          <span className="font-semibold text-primary">— Happy Client</span>
        </div>
      </div>
    </section>

    {/* CTA Section */}
    <section className="py-10 md:py-16 bg-primary text-white border-t border-primary/20">
      <div className="container-custom text-center max-w-2xl mx-auto">
        <h2 className="mb-4 md:mb-6 text-3xl font-bold flex items-center justify-center gap-2">
          <Smile className="text-white" size={32} /> Ready for a Friendly, Custom Website?
        </h2>
        <p className="text-base md:text-lg mb-6 md:mb-8 opacity-90">
          Let's make something amazing together! Reach out and see how easy (and fun) a new website can be.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4">
          <Button asChild size="lg" variant="secondary">
            <Link to="/contact">Say Hello</Link>
          </Button>
          <Button 
            asChild
            size="lg" 
            variant="outline" 
            className="border-white text-white hover:bg-white hover:text-primary"
          >
            <a 
              href={CALENDLY_URL} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Calendar className="mr-2 h-5 w-5" /> Book a Friendly Call
            </a>
          </Button>
        </div>
      </div>
    </section>
  </div>
);

export default WebDesign; 